/**
 * Table déclarative des capacités de style par type de widget.
 *
 * Pour chaque type de widget, décrit quelles SECTIONS l'onglet Style expose.
 * Le `Record<WidgetType, StyleCaps>` force l'exhaustivité : ajouter un type
 * dans `ALL_WIDGET_TYPES` oblige à statuer sur ses capacités ici.
 */

import type { WidgetType } from '../schemas';

export interface StyleCaps {
  /** Cadre : preset, bg, bordure, ombre, icône, badge (WidgetShell) — quasi tous. */
  appearance: boolean;
  /** Couleur principale (format.color) : gauge, progress_bar, metric_card. */
  color: boolean;
  /** Police/taille/couleur titre & valeur : famille metric_card. */
  typography: boolean;
  /** Alignement titre/valeur : famille metric_card. */
  alignment: boolean;
  /** Sens de tendance (trend_is_good) : metric_card. */
  trend: boolean;
  /** Options d'affichage chart (legend/grid/axes...) — PR1b, charts. */
  display: boolean;
  /** Couleur par série / axe Y — PR1b, charts. */
  series: boolean;
  /** Override de palette de séries (format.palette) — charts multi-séries / catégoriels. */
  palette: boolean;
  /** Section Métriques : liste des métriques avec label perso — widgets à métriques. */
  metrics: boolean;
  /** Le widget rend une comparaison → propose le select format de comparaison. */
  compareFormat: boolean;
  /** Sélecteur "Sens favorable" par métrique (metric_good_direction) : table, comparison_metric. */
  goodDirection: boolean;
  /** Options de tableau : total, gras, zébrage, densité, alignement colonnes — table uniquement. */
  tableOptions: boolean;
}

export const STYLE_CAPS: Record<WidgetType, StyleCaps> = {
  metric_card: {
    appearance: true,
    color: true,
    typography: true,
    alignment: true,
    trend: true,
    display: false,
    series: false,
    palette: false,
    metrics: true,
    compareFormat: true,
    goodDirection: false,
    tableOptions: false,
  },
  comparison_metric: {
    appearance: true,
    color: false,
    typography: true,
    alignment: true,
    trend: false,
    display: false,
    series: false,
    palette: false,
    metrics: true,
    compareFormat: true,
    goodDirection: true,
    tableOptions: false,
  },
  progress_bar: {
    appearance: true,
    color: true,
    typography: false,
    alignment: false,
    trend: false,
    display: false,
    series: false,
    palette: false,
    metrics: true,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  gauge: {
    appearance: true,
    color: true,
    typography: false,
    alignment: false,
    trend: false,
    display: false,
    series: false,
    palette: false,
    metrics: true,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  top_list: {
    appearance: true,
    color: false,
    typography: false,
    alignment: false,
    trend: false,
    display: true,
    series: false,
    palette: false,
    metrics: true,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  table: {
    appearance: true,
    color: false,
    typography: false,
    alignment: false,
    trend: false,
    display: true,
    series: false,
    palette: false,
    metrics: true,
    compareFormat: true,
    goodDirection: true,
    tableOptions: true,
  },
  rich_text: {
    appearance: true,
    color: false,
    typography: false,
    alignment: false,
    trend: false,
    display: false,
    series: false,
    palette: false,
    metrics: false,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  image_block: {
    appearance: true,
    color: false,
    typography: false,
    alignment: false,
    trend: false,
    display: false,
    series: false,
    palette: false,
    metrics: false,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  date_range_display: {
    appearance: true,
    color: false,
    typography: false,
    alignment: false,
    trend: false,
    display: false,
    series: false,
    palette: false,
    metrics: false,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  divider: {
    appearance: false,
    color: false,
    typography: false,
    alignment: false,
    trend: false,
    display: false,
    series: false,
    palette: false,
    metrics: false,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  // Charts cartésiens : légende/grille/axes/lissée + couleur unique + séries (couleur
  // par métrique + axe Y gauche/droite).
  line_chart: {
    appearance: true,
    color: true,
    typography: false,
    alignment: false,
    trend: false,
    display: true,
    series: true,
    palette: true,
    metrics: true,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  bar_chart: {
    appearance: true,
    color: true,
    typography: false,
    alignment: false,
    trend: false,
    display: true,
    series: true,
    palette: true,
    metrics: true,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  area_chart: {
    appearance: true,
    color: true,
    typography: false,
    alignment: false,
    trend: false,
    display: true,
    series: true,
    palette: true,
    metrics: true,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  // Donut : légende + labels. Couleur par catégorie (series_colors dynamique) = PR2.
  donut_pie: {
    appearance: true,
    color: false,
    typography: false,
    alignment: false,
    trend: false,
    display: true,
    series: false,
    palette: true,
    metrics: true,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  // Treemap/map/heatmap : pas d'options d'affichage ni de séries par métrique en PR1b.
  treemap: {
    appearance: true,
    color: false,
    typography: false,
    alignment: false,
    trend: false,
    display: false,
    series: false,
    palette: true,
    metrics: true,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  map: {
    appearance: true,
    color: false,
    typography: false,
    alignment: false,
    trend: false,
    display: false,
    series: false,
    palette: false,
    metrics: true,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
  calendar_heatmap: {
    appearance: true,
    color: false,
    typography: false,
    alignment: false,
    trend: false,
    display: false,
    series: false,
    palette: false,
    metrics: true,
    compareFormat: false,
    goodDirection: false,
    tableOptions: false,
  },
};
